export interface Application {
  id: string;
  name: string;
  description?: string | null;
  type: ApplicationType;
  customerId?: string;
  state: ApplicationState;
  datasourceCount?: number;
  region?: string;
  lock?: LockSate;
  allowedOrigins?: string[];
  isPublic?: boolean;
  embedTokens?: string[];
  appUrl?: string | null;
}

export enum LockStates {
  LOCKED = 'LOCKED',
  UNLOCKED = 'UNLOCKED',
  LOCKING = 'LOCKING',
  UNLOCKING = 'UNLOCKING',
}

export interface LockSate {
  state: LockStates;
  reason: string;
}

export enum ApplicationType {
  SITE_SEARCH = 'SITE_SEARCH',
  MACHINE_LEARNING = 'MACHINE_LEARNING',
  APP_STUDIO = 'APP_STUDIO',
  AI_ENDPOINT = 'AI_ENDPOINT',
  SIGNAL_STORE = 'SIGNAL_STORE',
}

export const AppTypeMap = {
  [ApplicationType.SITE_SEARCH]: 'Connected Search',
  [ApplicationType.MACHINE_LEARNING]: 'Lucidworks AI',
  [ApplicationType.APP_STUDIO]: 'Agent Studio',
  [ApplicationType.SIGNAL_STORE]: 'Signal Store',
};

export enum ApplicationState {
  REQUESTED = 'REQUESTED',
  READY_TO_CONFIGURE = 'READY_TO_CONFIGURE',
  ACTIVE = 'ACTIVE',
  DEACTIVATING = 'DEACTIVATING',
  INACTIVE = 'INACTIVE',
  ERROR = 'ERROR',
  UNKNOWN = 'UNKNOWN',
}

export interface UIConfig {
  authClientId: string;
  authIssuerUri: string;
  apiBasePath: string;
  launchDarklyEnvId: string;
  googleAnalyticsEnabled: boolean;
  googleAnalyticsTrackingId?: string;
  domain?: string;
  userPilotId?: string;
}

export type SignalTypes = 'visit' | 'query' | 'click' | 'cart-add' | 'purchase';

export interface SignalStatsCount {
  signal_type: SignalTypes;
  signal_count: number;
  application_id: string;
}

export interface SignalCounts {
  series: SignalStatsCount[];
  series_total: number;
}

export interface User {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  fullName?: string;
  status?: string;
  creationDate?: string;
  roles?: Role[];
}

export interface UserRoleView extends User {
  role: RoleType;
  roleMetaData?: { role: RoleType; customerAdmin: boolean; selectionDisabled: boolean };
  customerAdmin?: boolean;
  selectionDisabled?: boolean;
  app?: Application;
}

export interface Region {
  name: string;
  description: string;
  availability: string;
  visibility: RegionVisibility;
}

export type CellType = 'DATA' | 'FETCH' | 'TRAINING';
export type RegionVisibility = 'PUBLIC' | 'PRIVATE';
export type RoleLevel = 'APPLICATION' | 'CUSTOMER';
// TODO: remove this type as we are not going to maintain any knowledge of type of role in the UI anymore
export type RoleType = 'admin' | 'user' | 'NO ACCESS';

export interface Customer {
  id: string;
  name: string;
  capabilities: string[];
}

export interface Role {
  level: RoleLevel;
  id: string;
  role: RoleType | any;
  roleType?: 'SPRINGBOARD' | 'INTEGRATION';
  ownerId?: string;
  email?: string;
}

export interface CustomerRoleView extends Role {
  name?: string;
  selectionDisabled?: boolean;
}

export interface ApplicationRoleView extends Role {
  name?: string;
  roleMetaData?: { role: string; selectionDisabled: boolean };
  selectionDisabled?: boolean;
  appType?: ApplicationType;
  selectionOptions?: any[];
}

export interface CustomModelRoleView extends Role {
  roleMetaData?: { role: string; selectionDisabled: boolean };
  selectionDisabled?: boolean;
}

export interface IntegrationRoleView extends Role {
  name: string;
  integrations: Integration[];
  templateId: string;
  roleMetaData?: { role: string; selectionDisabled: boolean };
  selectionDisabled?: boolean;
}

export type MachineLearningModelStates = 'TRAINING' | 'TRAINING_FAILED' | 'AVAILABLE';
export type MachineLearningDeploymentStates = 'DEPLOYING' | 'DEPLOY_FAILED' | 'DEPLOYED' | 'DELETING' | 'DELETE_FAILED';

export interface CustomMachineLearningModel {
  id: string;
  modelType: string;
  vectorSize: number;
  category: string;
  description: string;
  region: string;
  trainingData: {
    catalog: string;
    signals: string;
  };
  config: {};
  state: MachineLearningModelStates;
  deployments?: CustomMachineLearningDeployment[];
  trainingStarted: string;
  trainingCompleted?: string;
  trainingMetrics?: {
    summary: {};
    epoch_metrics: {};
    final_metrics: {};
  };
  createdBy: string;
  name: string;
  errorMessage?: string;
}

export interface CustomMachineLearningDeployment {
  name: string;
  id: string;
  modelId: string;
  region: string;
  minReplicas?: number;
  maxReplicas?: number;
  state: MachineLearningDeploymentStates;
  deployedAt: string;
  lastUsed?: string;
}

export interface NewDeploymentFormData {
  modelId: string;
  region: string;
  minReplicas: number;
  maxReplicas: number;
}

export interface ServiceAccountCredentials {
  credentials: {
    serviceAccountKey: string;
  };
  urls: string[];
}

export interface Integration {
  id: string;
  name: string;
  tags: string[];
  clientId: string;
  loginUrls: string[];
  logoutUrls?: string[];
  template: Template;
  creationDate: string;
  application: Application;
  customer: Customer;
}

export interface Template {
  id: string;
  name: string;
  description: string;
  resourceLevel: 'APPLICATION' | 'CUSTOMER';
  code: string;
}

export interface SpringboardRoleMeta {
  id: string;
  name: string;
  description: string;
  resourceLevel: 'APPLICATION' | 'CUSTOMER';
  workloadType?: {
    domain: string;
    type: string;
  };
}

export interface IntegrationRoleTemplateMeta extends SpringboardRoleMeta {
  resourceLevel: RoleLevel;
}

export interface IntegrationRoles {
  integrationTemplate: IntegrationRoleTemplateMeta;
  roles: Array<Omit<SpringboardRoleMeta, 'resourceLevel'>>;
}

export interface SupportedUserRoles {
  springboardRoles: SpringboardRoleMeta[];
  integrationRoles: IntegrationRoles[];
}

export enum SpringboardRoleTypes {
  APPLICATION = 'APPLICATION',
  CUSTOMER = 'CUSTOMER',
}

export interface NewModelFormData {
  name: string;
  modelType: string;
  region: string;
  trainingData: {
    signals: string;
    catalog: string;
  };
  config?: {
    'trainer_config.encoder_config.rnn_names_list': string[];
    'trainer_config.encoder_config.rnn_units_list': number[];
    'trainer_config/text_processor_config'?: string;
    'trainer_config/encoder_config'?: string;
  };
  trainingDataCredentials?: {
    serviceAccountKey: string;
  };
}

export interface DeploymentLastUsed {
  timestamp_latest: string;
}

export interface Capability {
  id: string;
  displayName: string;
  description: string | null;
  domain: string | null;
  aliases: string[];
  capabilities: Capability[];
  topLevel: boolean;
}

export interface ManagedFusion {
  id: string;
  name: string;
  environment: string;
  notes: string;
  tags: string[];
  type: string;
  urlFragment: string;
  useCase: string;
  fusionVersion: string;
  solrVersion: string;
  k8sVersion: string;
  region: FusionEnvironment;
  loginUrls: string[];
  logoutUrls: string[];
  externalMetricsLink: string;
  customer: Pick<Customer, 'id' | 'name'>;
  fusionConfigurationPromotionEnabled?: boolean;
}

export interface FusionEnvironment {
  vendorId: string;
  regionId: string;
  location: string;
}

export interface BaseSelfHostedFusionIntegration {
  id: string;
  name: string;
  environment: string;
  tags?: string[];
  type?: string;
  apiUrl: string;
  fusionConfigurationPromotionEnabled?: boolean;
  githubBranch?: string;
  githubOrganization?: string;
  githubRepository?: string;
  githubBranchURL?: string;
  githubToken?: string;
}

export interface SelfHostedFusionIntegration extends BaseSelfHostedFusionIntegration {
  fusionVersion: string;
  solrVersion: string;
  connectionStatus: string;
  creationDate: string;
  jwksUrl: string;
  issuerUrl: string;
  additionalUrls?: string[];
  fusionConfigurationPromotionEnabled?: boolean;
}

export enum fusionInstanceTypes {
  MANAGED_FUSION = 'MANAGED_FUSION',
  SELF_HOSTED_FUSION = 'SELF_HOSTED_FUSION',
}

export interface OIDCClientUserSSOForm {
  name: string;
  id?: string;
  emailDomains: string[];
  clientId: string;
  clientSecret: string;
  configurationUrl: string;
  customDataFields?: {
    issuerUrl: string;
    authorizationUrl: string;
    tokenUrl: string;
    userinfoUrl: string;
    jwksUrl: string;
  };
}

export interface OIDCClientUserSSOResponse {
  id: string;
  name: string;
  type: string;
  customer: Customer;
  emailDomains: string[];
  state: string;
  description: string;
  configurationUrl: string;
  customDataFields: {
    issuerUrl: string;
    authorizationUrl: string;
    tokenUrl: string;
    userinfoUrl: string;
    jwksUrl: string;
  };
  credentials: {
    clientId: string;
    clientSecret: string;
  };
  identityProviderAuthorizeUrl: string;
  identityProviderRedirectUri: string;
}

export interface SAMLClientUserSSOForm {
  name: string;
  id?: string;
  emailDomains: string[];
  metadataUrl: string;
  customDataFields?: {
    ssoUrl: string;
    issuerUrl: string;
    certificate: string;
  };
}

export interface SAMLClientUserSSOResponse {
  id: string;
  name: string;
  type: string;
  customer: Customer;
  emailDomains: string[];
  state: string;
  description: string;
  metadataUrl: string;
  customDataFields: {
    ssoUrl: string;
    issuerUrl: string;
    certificate: string;
  };
  identityProviderAssertionConsumersServiceUrl: string;
  identityProviderAudienceUri: string;
}

export interface ExperienceManagerInstance {
  id: string;
  region: string;
  name: string;
  state: string; //TODO: Get enum from backend and as a type here
  fusionInstance: string;
  fusionAppId: string;
}

export interface ServiceAccountRequest {
  name: string;
  description: string;
  tags: string[];
  type: string;
  roles?: Role[];
}

export interface ServiceAccount {
  id: string;
  name: string;
  description: string;
  customer: Customer;
  creationDate: string;
  createdBy: string;
  tags: string[];
  type: string;
  roles?: Role[];
  clientId: string;
  clientSecret: string;
}

export interface ServiceAccountForm {
  name: string;
  type: string;
  description?: string;
  tags?: string[];
  id?: string;
}

export interface ClientCredentials {
  clientId: string;
  clientSecret: string;
  authorizationServer: string;
}

export interface LucidworksAIIntegration {
  application: {
    id: string;
    name: string;
  };
  clientId: string;
  creationDate: string;
  customer: Customer;
  id: string;
  integratedSystemIds?: [];
  loginUrls: [];
  logoutUrls: [];
  name: string;
  tags: [];
  template: {
    authorizationFlows: {
      grantTypes: [];
      id: string;
      name: string;
      scopes: [];
    }[];
    code: string;
    description: string;
    id: string;
    name: string;
    resourceLevel: string;
  };
}

export interface LucidworksAIPredictions {
  predictions: LucidworksAIPrediction[];
}

export interface LucidworksAIPrediction {
  response: string;
  tokensUsed: {};
}

export interface LWAIEndpoint {
  id: string;
  name: string;
}

export interface LWAIModel {
  id: string;
  modelProvider: string;
  modelType: string;
  name: string;
}

export interface CombinedEndpointAndModels extends LWAIEndpoint {
  models: LWAIModel[];
}

export interface CustomerSite {
  id: string;
  name: string;
  creationDate: string;
  lastModified?: string;
}

export interface SiteEnvironment extends CustomerSite {
  integratedSystemId: string;
  fusionAppId: string;
  searchService: { endpoint: string };
  typeaheadService: { endpoint: string };
}

export interface EmbeddablesGenAIModelConfig {
  last_requested_on: string;
  model_name: string;
  total_requests: number;
  total_tokens_in: number;
  total_tokens_out: number;
}
export interface EmbeddableModelRequestSeries {
  model_name: string;
  total_requests: {
    time: string;
    value: { int: number };
  }[];
}

export interface EmbeddableModelUsageSummary {
  legend: string;
  series: {
    time: string;
    value: { int: number };
  }[];
  total_main: { int: number };
  total_previous: { int: number };
}

export interface AgentUsageResponse {
  embeddable_gen_ai_models: EmbeddablesGenAIModelConfig[];
  embeddable_model_requests_series: {
    total_requests_series: EmbeddableModelRequestSeries[];
    average_request: number;
  };
  embeddable_model_usage_summary: EmbeddableModelUsageSummary[];
}
// --- Model Comparison / Evaluation types ---

// Detailed backend states: informational only. Drive UI behavior from `displayState`.
export type ModelComparisonState =
  | 'STAGING'
  | 'QUEUED'
  | 'IN_PROGRESS'
  | 'SUMMARIZING'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLING'
  | 'CANCELLED';
export type ModelComparisonGoal = 'FIELD_VARIANTS' | 'MODELS' | 'MODEL_CONFIG_SETTINGS';
export type ModelEvaluationType = 'CUSTOM' | 'PRETRAINED';
export type ModelEvaluationState =
  | 'CREATED'
  | 'QUEUED'
  | 'IN_PROGRESS'
  | 'STOPPING'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED';
// The UI-facing alias of every comparison and evaluation state.
export type DisplayState = 'in_progress' | 'complete' | 'failed' | 'cancelled';
export type ComparisonErrorCode =
  | 'STAGING_CREDENTIALS_INVALID'
  | 'STAGING_ACCESS_DENIED'
  | 'STAGING_SOURCE_NOT_FOUND'
  | 'STAGING_SOURCE_EMPTY'
  | 'STAGING_TRANSIENT_ERROR'
  | 'STAGING_TIMEOUT'
  | 'EVALUATION_FAILED'
  | 'SUMMARY_FAILED'
  | 'SUMMARY_TIMEOUT';

export type ConfigMap = Record<string, string | number | boolean | null>;
export type MetricsMap = Record<string, number | string | null>;

export interface ModelEvaluationRequest {
  modelId: string;
  evaluationType: ModelEvaluationType;
  modelConfig?: ConfigMap;
  config?: ConfigMap;
}

export interface DatasetStats {
  index_size: number;
  query_size: number;
}

export interface ModelEvaluationResponse {
  id: string;
  modelId: string;
  evaluationType: ModelEvaluationType;
  modelUri: string;
  modelName: string | null;
  textEncoderConfig: ConfigMap | null;
  modelConfig: ConfigMap;
  config: ConfigMap | null;
  state: ModelEvaluationState;
  displayState: DisplayState;
  started: string | null;
  completed: string | null;
  errorMessage: string | null;
  rankingMetrics: MetricsMap | null;
  timeMetrics: MetricsMap | null;
  datasetStats?: DatasetStats;
}

export interface EvaluationDatasetRequest {
  index: string;
  query?: string;
}

export interface EvaluationDatasetResponse {
  index: string;
  query: string | null;
}

export interface AvailableModelResponse {
  modelId: string;
  displayName: string;
  evaluationType: ModelEvaluationType;
  vectorDim: number;
}

export interface ModelComparisonCreateRequest {
  name: string;
  region: string;
  modelEvaluations: ModelEvaluationRequest[];
  dataset: EvaluationDatasetRequest;
  config: ConfigMap;
  monitorMetric?: string;
  comparisonGoal?: ModelComparisonGoal;
  trainingDataCredentials?: { serviceAccountKey: string };
}

export interface WinnerSettings {
  model_id?: string;
  field_name?: string | null;
  model_config?: ConfigMap;
  display_name?: string;
}

export interface RecommendationDataset {
  dataset_name?: string;
  sort_metric?: string;
  quality_pick_score?: number;
  quality_pick_settings?: WinnerSettings;
}

export interface ModelEvaluationDetail {
  id: string;
  model_id: string;
  model_name: string;
  evaluation_type: string;
  score: number;
  metric: string;
  latency_ms: number | null;
  model_config: Record<string, any>;
  config: Record<string, string> | null;
}

export interface ComparisonRecommendation {
  winner: ModelEvaluationDetail;
  runner_up?: ModelEvaluationDetail | null;
  explanation: string;
  sort_metric: string;
  latency_metric: string;
}

export interface ModelComparisonResponse {
  id: string;
  name: string;
  region: string;
  cellName: string;
  state: ModelComparisonState;
  displayState: DisplayState;
  errorCode?: ComparisonErrorCode | null;
  errorMessage?: string | null;
  started: string;
  completed: string | null;
  customerId: string;
  createdBy: string;
  modelEvaluations: ModelEvaluationResponse[];
  dataset: EvaluationDatasetResponse;
  config: ConfigMap;
  monitorMetric: string | null;
  comparisonGoal: ModelComparisonGoal | null;
  summary: string | null;
  recommendation: ComparisonRecommendation | null;
  explanations: Record<string, string> | null;
}

export interface ModelComparisonListResponse {
  content: ModelComparisonResponse[];
  totalElements?: number;
  totalPages?: number;
  size?: number;
  number?: number;
}
